import React, { useMemo } from 'react';
import {
  Activity, CheckCircle, Clock, AlertCircle, Layers,
  Ticket, Timer, PlusCircle, List, FileBarChart,
  ShoppingCart, Coffee, History, Banknote, DollarSign,
  BookOpen, TrendingUp, Users, Building2, MapPin,
  Printer, Settings, ArrowRight
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const formatDuration = (ms) => {
  if (!ms && ms !== 0) return '-';
  if (ms < 1000) return '< 1m';
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const parts = [];
  if (hours > 0) parts.push(hours + 'j');
  if (minutes % 60 > 0) parts.push((minutes % 60) + 'm');
  return parts.join(' ') || '< 1m';
};

const MobileDashboard = ({
  activities = [],
  stats = {},
  tickets = [],
  ticketStats = {},
  ticketOpenCount = 0,
  onNavigate,
}) => {
  const { user, hasPermission } = useAuth();

  // ─── Stats ──────────────────────────────────────────────
  const totalInfo = stats?.total || {};
  const activityStats = {
    total: totalInfo.total || activities.length,
    completed: totalInfo.completed || activities.filter(a => a.status === 'Completed').length,
    inProgress: totalInfo.inProgress || activities.filter(a => a.status === 'In Progress').length,
    pending: totalInfo.pending || activities.filter(a => a.status === 'Pending').length,
  };

  const completionRate = activityStats.total > 0
    ? Math.round((activityStats.completed / activityStats.total) * 100)
    : 0;

  const avgActualDuration = useMemo(() => {
    const completed = activities.filter(a => a.status === 'Completed' && a.actualDuration);
    return completed.length > 0
      ? completed.reduce((sum, a) => sum + a.actualDuration, 0) / completed.length
      : 0;
  }, [activities]);

  const tStats = ticketStats?.total || { total: 0, open: 0, inProgress: 0, resolved: 0 };

  // ─── Quick Stats Cards ─────────────────────────────────
  const quickStats = [
    {
      label: 'Activities',
      value: activityStats.total,
      icon: Activity,
      color: 'blue',
      onClick: () => onNavigate('list'),
    },
    {
      label: 'Done',
      value: activityStats.completed,
      icon: CheckCircle,
      color: completionRate >= 80 ? 'green' : completionRate >= 50 ? 'yellow' : 'red',
      onClick: () => onNavigate('stats'),
    },
    {
      label: 'Tickets',
      value: tStats.total,
      icon: Ticket,
      color: tStats.open > 0 ? 'red' : 'indigo',
      onClick: () => onNavigate('tickets'),
      badge: tStats.open > 0 ? tStats.open : 0,
    },
    {
      label: 'Open',
      value: tStats.open,
      icon: AlertCircle,
      color: tStats.open > 0 ? 'red' : 'green',
      onClick: () => onNavigate('tickets'),
    },
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: { bg: 'bg-blue-50', text: 'text-blue-600', iconBg: 'bg-blue-100', gradient: 'from-blue-500 to-blue-400' },
      green: { bg: 'bg-green-50', text: 'text-green-600', iconBg: 'bg-green-100', gradient: 'from-green-500 to-emerald-400' },
      red: { bg: 'bg-red-50', text: 'text-red-600', iconBg: 'bg-red-100', gradient: 'from-red-500 to-rose-400' },
      yellow: { bg: 'bg-yellow-50', text: 'text-yellow-600', iconBg: 'bg-yellow-100', gradient: 'from-yellow-500 to-orange-400' },
      indigo: { bg: 'bg-indigo-50', text: 'text-indigo-600', iconBg: 'bg-indigo-100', gradient: 'from-indigo-500 to-indigo-400' },
      purple: { bg: 'bg-purple-50', text: 'text-purple-600', iconBg: 'bg-purple-100', gradient: 'from-purple-500 to-purple-400' },
    };
    return colors[color] || colors.blue;
  };

  // ─── Grid Menu Items ───────────────────────────────────
  const gridMenuItems = [
    {
      id: 'add-activity',
      label: 'Add Activity',
      icon: PlusCircle,
      color: 'blue',
      view: 'add',
      roles: ['admin', 'user'],
      menuId: 'ACTIVITY_ADD',
    },
    {
      id: 'list',
      label: 'Activities',
      icon: List,
      color: 'green',
      view: 'list',
      roles: ['admin', 'user', 'viewer'],
      menuId: 'ACTIVITY_LIST',
    },
    {
      id: 'new-ticket',
      label: 'New Ticket',
      icon: Ticket,
      color: 'indigo',
      view: 'new-ticket',
      roles: ['admin', 'user'],
      menuId: 'TICKET_NEW',
    },
    {
      id: 'cafe-pos',
      label: 'POS Kasir',
      icon: ShoppingCart,
      color: 'orange',
      view: 'cafe-pos',
      roles: ['admin', 'user'],
      menuId: 'CAFE_POS',
    },
    {
      id: 'report',
      label: 'Laporan',
      icon: FileBarChart,
      color: 'purple',
      view: 'stats',
      roles: ['admin', 'user'],
      menuId: 'ACTIVITY_REPORT',
    },
    {
      id: 'transaksi',
      label: 'Keuangan',
      icon: DollarSign,
      color: 'emerald',
      view: 'transaksi',
      roles: ['admin', 'user'],
    },
    {
      id: 'cafe-menu',
      label: 'Menu Cafe',
      icon: Coffee,
      color: 'amber',
      view: 'cafe-dashboard',
      roles: ['admin', 'user'],
    },
    {
      id: 'users',
      label: 'Pengguna',
      icon: Users,
      color: 'red',
      view: 'users',
      roles: ['admin'],
    },
  ];

  const visibleGridItems = gridMenuItems.filter(item =>
    item.roles.includes(user?.role) &&
    (!item.menuId || hasPermission(item.menuId, 'active'))
  );

  const getGridColor = (color) => {
    const colors = {
      blue: { bg: 'bg-blue-50', iconBg: 'bg-blue-100', text: 'text-blue-600' },
      green: { bg: 'bg-green-50', iconBg: 'bg-green-100', text: 'text-green-600' },
      indigo: { bg: 'bg-indigo-50', iconBg: 'bg-indigo-100', text: 'text-indigo-600' },
      orange: { bg: 'bg-orange-50', iconBg: 'bg-orange-100', text: 'text-orange-600' },
      purple: { bg: 'bg-purple-50', iconBg: 'bg-purple-100', text: 'text-purple-600' },
      emerald: { bg: 'bg-emerald-50', iconBg: 'bg-emerald-100', text: 'text-emerald-600' },
      amber: { bg: 'bg-amber-50', iconBg: 'bg-amber-100', text: 'text-amber-600' },
      red: { bg: 'bg-red-50', iconBg: 'bg-red-100', text: 'text-red-600' },
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="pb-4 space-y-4">
      {/* ─── Greeting ──────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-gray-400">Welcome back,</p>
          <p className="text-lg font-bold text-gray-900">{user?.name || user?.username}</p>
        </div>
        <div className="flex items-center space-x-1.5">
          <div className="bg-blue-50 rounded-lg px-2.5 py-1.5 flex items-center space-x-1">
            <Activity className="w-3.5 h-3.5 text-blue-500" />
            <span className="text-xs font-bold text-blue-600">{activityStats.total}</span>
          </div>
          <div className="bg-indigo-50 rounded-lg px-2.5 py-1.5 flex items-center space-x-1">
            <Ticket className="w-3.5 h-3.5 text-indigo-500" />
            <span className="text-xs font-bold text-indigo-600">{tStats.total}</span>
          </div>
        </div>
      </div>

      {/* ─── Quick Stats Row (horizontal scroll) ──────── */}
      <div className="flex space-x-3 overflow-x-auto pb-1 -mx-4 px-4 scrollbar-hide">
        {quickStats.map((stat, i) => {
          const colors = getColorClasses(stat.color);
          return (
            <button
              key={stat.label}
              onClick={stat.onClick}
              className="flex-shrink-0 w-[calc(50%-6px)] min-w-[140px] bg-white rounded-xl border border-gray-100 p-3.5 shadow-sm hover:shadow-md transition-all active:scale-95"
            >
              <div className="flex items-start justify-between mb-2">
                <div className={`p-2 rounded-lg ${colors.iconBg}`}>
                  <stat.icon className={`w-4 h-4 ${colors.text}`} />
                </div>
                {stat.badge > 0 && (
                  <span className="text-[10px] font-bold text-white bg-red-500 px-1.5 py-0.5 rounded-full">
                    {stat.badge}
                  </span>
                )}
              </div>
              <p className="text-xl font-extrabold text-gray-900">{stat.value}</p>
              <p className="text-[11px] text-gray-500 font-medium">{stat.label}</p>
            </button>
          );
        })}
      </div>

      {/* ─── Stats Highlights ──────────────────────────── */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-500 rounded-xl p-4 text-white shadow-lg">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-bold opacity-90">Ringkasan</h3>
          <button onClick={() => onNavigate('stats')} className="flex items-center space-x-1 text-[11px] font-semibold opacity-80 hover:opacity-100">
            <span>Detail</span>
            <ArrowRight className="w-3 h-3" />
          </button>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <p className="text-[10px] opacity-70 font-medium">Completed</p>
            <p className="text-lg font-bold">{activityStats.completed}</p>
            <div className="mt-1 w-full bg-white/20 rounded-full h-1">
              <div
                className="bg-white rounded-full h-1 transition-all"
                style={{ width: `${completionRate}%` }}
              />
            </div>
          </div>
          <div>
            <p className="text-[10px] opacity-70 font-medium">Progress</p>
            <p className="text-lg font-bold">{activityStats.inProgress}</p>
          </div>
          <div>
            <p className="text-[10px] opacity-70 font-medium">Rata-rata</p>
            <p className="text-lg font-bold text-[16px]">{avgActualDuration > 0 ? formatDuration(avgActualDuration) : '-'}</p>
          </div>
        </div>
      </div>

      {/* ─── Grid Menu ──────────────────────────────────── */}
      <div>
        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 px-0.5">
          Quick Menu
        </h3>
        <div className="grid grid-cols-4 gap-2.5">
          {visibleGridItems.map((item) => {
            const colors = getGridColor(item.color);
            return (
              <button
                key={item.id}
                onClick={() => {
                  if (item.id === 'add-activity') onNavigate('add');
                  else onNavigate(item.view);
                }}
                className="flex flex-col items-center space-y-1 p-2.5 rounded-xl bg-white border border-gray-50 shadow-sm hover:shadow-md active:scale-90 transition-all"
              >
                <div className={`p-2 rounded-xl ${colors.iconBg}`}>
                  <item.icon className={`w-5 h-5 ${colors.text}`} />
                </div>
                <span className="text-[10px] font-medium text-gray-600 text-center leading-tight">
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ─── Recent Activities (compact) ─────────────────── */}
      {activities.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-0.5">
              Recent
            </h3>
            <button onClick={() => onNavigate('list')} className="text-[11px] font-semibold text-blue-600 flex items-center space-x-0.5">
              <span>All</span>
              <ArrowRight className="w-3 h-3" />
            </button>
          </div>
          <div className="space-y-1">
            {activities.slice(0, 5).map((a) => (
              <div
                key={a._id || a.id}
                onClick={() => onNavigate('list')}
                className="flex items-center space-x-3 p-2.5 rounded-xl bg-white border border-gray-50 active:bg-gray-50 transition-all"
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-[10px] font-bold text-white ${
                  a.status === 'Completed' ? 'bg-gradient-to-br from-green-500 to-emerald-600' :
                  a.status === 'In Progress' ? 'bg-gradient-to-br from-yellow-500 to-orange-500' :
                  'bg-gradient-to-br from-gray-400 to-gray-500'
                }`}>
                  {a.title?.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{a.title}</p>
                  <p className="text-[10px] text-gray-400">{a.category}</p>
                </div>
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                  a.status === 'Completed' ? 'bg-green-50 text-green-700' :
                  a.status === 'In Progress' ? 'bg-yellow-50 text-yellow-700' :
                  'bg-gray-100 text-gray-600'
                }`}>
                  {a.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ─── Empty State ─────────────────────────────────── */}
      {activities.length === 0 && tickets.length === 0 && (
        <div className="text-center py-10">
          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
            <Activity className="w-8 h-8 text-gray-300" />
          </div>
          <p className="text-gray-500 font-medium text-sm">Welcome!</p>
          <p className="text-gray-400 text-xs mt-0.5">Mulai dengan aktivitas atau tiket pertama</p>
          <button
            onClick={() => onNavigate('add')}
            className="mt-4 px-5 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-xl shadow-lg shadow-blue-200 active:scale-95 transition-all"
          >
            + Buat Activity
          </button>
        </div>
      )}
    </div>
  );
};

export default MobileDashboard;
